import React from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import { PageHeader } from '../../../ui';
import StepperAdesao from '../../../components/adesao/StepperAdesao';
import FormularioAssociado, {
  DadosAssociado,
} from '../../../components/associado/FormularioAssociado';

import { addCarroData, updateAssociadoData } from '../../../store/associadoSlice';
import { Associado } from '../../../api/associado/associadoApi';

import { CARROSUBSTITUIR } from '../../carro/routes';

/**
 * `/associado/substituir` — **etapa 1 da Substituição de Titular** (at-58, frente C).
 *
 * 🔴 Como a irmã `/associado/edit`, o nome do arquivo não corresponde ao fluxo:
 * é a primeira etapa da **troca de titular**, alcançada pela opção 4 do menu
 * global, e daqui se segue para `/carro/substituir`. Ver A4 no `plan-c`.
 *
 * ## As diferenças em relação às irmãs
 *
 *   • o primeiro bloco chama-se **"Identificação do Novo Associado"** — é a
 *     pessoa que vai *assumir* o veículo;
 *   • o "Salvar" leva a `/carro/substituir`;
 *   • ao encontrar o associado, guarda o **`codigoAssociadoNovo`** no slice do
 *     carro: é ele que o backend usa para reconhecer a troca de titular
 *     (`derivarTipoRegistro`, at-28) e reenviar o veículo ao SGA;
 *   • oferece **"Buscar no SGA"** quando o CPF não está no SAAC (at-32, item 30):
 *     o novo titular pode já ser associado no SGA sem nunca ter passado por aqui,
 *     e importar evita redigitar e mantém o vínculo correto;
 *   • **não** grava rascunho e **não** faz a checagem antecipada de CPF (D3) —
 *     aqui o CPF existir é o caminho normal.
 */
const AssociadoSubstituirPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const voltar = useVoltar();

  /**
   * O código do associado no SGA vira `codigoAssociadoNovo` do carro.
   *
   * ⚠️ É o marcador da troca de titular: sem ele o backend trataria o envio como
   * uma adesão comum, e a tela `/ti/envios` classificaria o registro como
   * `ADESAO` em vez de `TROCA_TITULAR`.
   */
  const guardarCodigoDoNovoTitular = (associado: Associado) => {
    const codigo =
      (associado as any).codigoAssociado ?? (associado as any).codigo_associado ?? null;

    dispatch(addCarroData({ codigoAssociadoNovo: codigo ? String(codigo) : null }));
  };

  const salvar = (dados: DadosAssociado) => {
    dispatch(updateAssociadoData(dados));

    navigate(CARROSUBSTITUIR());
  };

  return (
    <ShellSaac
      cabecalho={
        <PageHeader
          titulo="Associado"
          sobretitulo="Substituição de Titular"
          aoVoltar={voltar}
        />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-3xl md:tw-p-6">
          <div className="tw-bg-white md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-border-b tw-border-superficie-borda">
              <StepperAdesao etapa={1} fluxo={"SUBSTITUICAO_TITULAR"} />
            </div>

            <div className="tw-p-4 md:tw-p-6">
              <FormularioAssociado
                tituloIdentificacao="Identificação do Novo Associado"
                rotuloAcao="Continuar — Veículo"
                comBuscaLocal
                comImportacaoSga
                aoEncontrarLocal={guardarCodigoDoNovoTitular}
                aoSalvar={salvar}
              />
            </div>
          </div>
        </div>
      </div>
    </ShellSaac>
  );
};

export default AssociadoSubstituirPage;
